import type { UserData } from '@beat-venues/common-types';
import { Button, Heading, Text } from '@beat-venues/ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UpdateUserForm } from './update-user-form';
import { UserDetails } from './user-details';

export interface UserProps {
    user: Omit<UserData, 'sfContactId'>;
    updateUser: (user: Omit<UserData, 'sfContactId'>) => void;
    isPending?: boolean;
    isSuccess?: boolean;
    isError?: boolean;
    title?: string;
    userAbbrev?: string;
}

export const User = ({
    user,
    updateUser,
    title,
    userAbbrev,
    isPending,
    isSuccess,
    isError,
}: UserProps): JSX.Element => {
    const [editable, setEditable] = useState(false);
    const [isCanceled, setIsCanceled] = useState(false);

    const { t } = useTranslation();
    const { i18n } = useTranslation();

    const handleDeleteAccount = (value?: string, language?: string) => {
        const url = `https://helpcenter.beatvenues.be/hc/${language}/requests/new?tf_16286450551825=corporate_about_beat&tf_360030207777=__dc.beat_venues_overkoepelend__&tf_7421003635089=&tf_anonymous_requester_email=${value}&tf_collaborators=&tf_description=&tf_priority=&tf_subject=&tf_type=&ticket_form_id=16285795899281`;
        window.open(url);
    };

    useEffect(() => {
        if (isSuccess && editable) {
            setEditable(false);
        }
    }, [isSuccess]);

    return (
        <div className="flex w-full flex-row-reverse justify-evenly gap-0 md:flex-row">
            {userAbbrev && (
                <div className="bg-gradient-myBeat hidden h-20 w-20 rounded-full p-1 md:flex">
                    <span className="text-tile m-auto flex h-full w-full min-w-[3.5rem] items-center justify-center rounded-full text-4xl font-medium uppercase leading-8">
                        {userAbbrev}
                    </span>
                </div>
            )}
            <div className="w-full pt-4 md:w-3/5">
                <div className="flex flex-col">
                    <Heading as="h1" className="font-light xl:ml-0">
                        {title}
                    </Heading>
                    <Text>{user?.email}</Text>
                    {isSuccess && !isCanceled && !editable && (
                        <Text className="bg-tt-brand-700 my-4 rounded-sm p-2 pl-5">
                            &#10003; {t('messages.successUpdate')}
                        </Text>
                    )}
                </div>
                {!editable ? (
                    <UserDetails
                        updateUserDetails={() => {
                            setEditable(true);
                            setIsCanceled(false);
                        }}
                        user={user}
                    />
                ) : (
                    <UpdateUserForm
                        isError={isError}
                        isPending={isPending}
                        user={user}
                        onExit={() => {
                            setEditable(false);
                            setIsCanceled(true);
                        }}
                        onSubmit={updateUser}
                    />
                )}
                {!editable && (
                    <div className="mt-36 flex w-full flex-col gap-4 md:mx-auto">
                        <Text className="text-2xl">
                            {t('account.removeAccountTitle')}
                        </Text>
                        <Button
                            className="w-fit"
                            intent="quinary"
                            onClick={() =>
                                handleDeleteAccount(
                                    user.email ?? '',
                                    i18n.language
                                )
                            }
                        >
                            {t('account.removeAccount')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};
